// Screen: "Membership"
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUserSubscription } from '../apiClient';
import { isNative } from '../utils/platform';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const VENMO_HANDLE = 'euchretable';

const FREE_FEATURES = [
  { label: 'Play vs AI (3P & 4P)', included: true },
  { label: '5 Bucks daily login drip', included: true },
  { label: 'Public leaderboards', included: true },
  { label: 'Basic stats (My Stats + Leaderboard)', included: true },
  { label: 'Join community rooms', included: true },
  { label: 'Join clubs', included: true },
  { label: '7-day match history', included: true },
  { label: 'Ad-free experience', included: false },
  { label: 'Create rooms & clubs', included: false },
  { label: 'Advanced analytics (5 tabs)', included: false },
  { label: '30-day match history', included: false },
  { label: 'Monthly 50 Bucks bonus', included: false },
  { label: 'Exclusive cosmetics', included: false },
  { label: 'Community Supporter badge', included: false },
  { label: 'Green profile accent', included: false },
];

const CLUB_FEATURES = [
  { label: 'Play vs AI (3P & 4P)', included: true },
  { label: '8 Bucks daily login drip', included: true },
  { label: 'Public leaderboards', included: true },
  { label: 'All 5 analytics tabs', included: true },
  { label: 'Create & join community rooms', included: true },
  { label: 'Create & join clubs', included: true },
  { label: '30-day match history', included: true },
  { label: 'Ad-free experience', included: true },
  { label: 'Monthly 50 Bucks bonus', included: true },
  { label: 'Exclusive subscriber cosmetics', included: true },
  { label: 'Community Supporter badge', included: true },
  { label: 'Green profile accent', included: true },
  { label: 'Priority support', included: true },
];

export default function MembershipPage() {
  const { token, user, isClubMember, updateUser } = useAuth();
  const isMobile = useIsMobile();
  const [subscription, setSubscription] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState(null); // { success, message }
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => {
    if (token) {
      getUserSubscription(token).then(setSubscription);
    }
  }, [token]);

  const currentTier = subscription?.tier || user?.membershipTier || 'free';

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoStatus(null);
    try {
      const res = await fetch('/api/users/redeem-promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: promoCode.trim().toUpperCase() }),
      });
      const data = await res.json();
      if (data.success) {
        setPromoStatus({ success: true, message: data.message });
        setPromoCode('');
        if (data.clubGranted) {
          updateUser({ membershipTier: 'club_member', clubTierPromoExpiresAt: data.clubTierExpiresAt });
          getUserSubscription(token).then(setSubscription);
        }
      } else {
        setPromoStatus({ success: false, message: data.error || 'Invalid promo code' });
      }
    } catch (e) {
      setPromoStatus({ success: false, message: 'Something went wrong. Please try again.' });
    } finally {
      setPromoLoading(false);
    }
  };

  const openVenmo = (amount, note) => {
    const encodedNote = encodeURIComponent(note);
    const url = isNative || isMobile
      ? `venmo://paycharge?txn=pay&recipients=${VENMO_HANDLE}&amount=${amount}&note=${encodedNote}`
      : `https://venmo.com/${VENMO_HANDLE}?txn=pay&amount=${amount}&note=${encodedNote}`;
    window.open(url, '_blank');
  };

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 900, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 24 }}>Membership</h1>

      {/* Current tier banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1c2333, #0d1117)',
        border: `1px solid ${isClubMember ? '#3fb950' : '#30363d'}`,
        borderRadius: 12, padding: isMobile ? 16 : 20, marginBottom: 24, textAlign: 'center'
      }}>
        <div style={{ fontSize: 11, color: '#8b949e', textTransform: 'uppercase', marginBottom: 4 }}>Current Plan</div>
        <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 700, color: isClubMember ? '#3fb950' : '#8b949e' }}>
          {isClubMember ? 'Club Member' : 'Free'}
        </div>
        {isClubMember && (subscription?.subscriptionExpires || user?.clubTierPromoExpiresAt) && (
          <div style={{ fontSize: 12, color: '#8b949e', marginTop: 4 }}>
            {user?.clubTierPromoExpiresAt ? 'Promo expires: ' : 'Expires: '}
            {new Date(user?.clubTierPromoExpiresAt || subscription?.subscriptionExpires).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Two-tier comparison */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 16, marginBottom: 32
      }}>
        {/* Free tier */}
        <div style={{
          background: '#161b22', border: '1px solid #30363d',
          borderRadius: 12, padding: isMobile ? 16 : 24
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#8b949e', marginBottom: 4 }}>Free</div>
            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: '#e2e8f0' }}>$0</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Forever free</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            {FREE_FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: f.included ? '#c9d1d9' : '#484f58' }}>
                <span style={{ color: f.included ? '#3fb950' : '#484f58', fontSize: 14 }}>{f.included ? '\u2713' : '\u2717'}</span>
                {f.label}
              </div>
            ))}
          </div>
          {currentTier === 'free' && (
            <div style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid #30363d',
              borderRadius: 8, padding: '8px 16px', textAlign: 'center',
              color: '#8b949e', fontSize: 13, fontWeight: 600
            }}>Current Plan</div>
          )}
        </div>

        {/* Club Member tier */}
        <div style={{
          background: '#161b22', border: '2px solid #3fb950',
          borderRadius: 12, padding: isMobile ? 16 : 24, position: 'relative'
        }}>
          <div style={{
            position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
            background: '#3fb950', color: '#000', padding: '2px 12px', borderRadius: 10,
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase'
          }}>Recommended</div>

          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#3fb950', marginBottom: 4 }}>Club Member</div>
            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: '#e2e8f0' }}>
              $4.99<span style={{ fontSize: 14, color: '#8b949e' }}>/mo</span>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>or $39.99/year (save 33%)</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            {CLUB_FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#c9d1d9' }}>
                <span style={{ color: '#3fb950', fontSize: 14 }}>{'\u2713'}</span>
                {f.label}
              </div>
            ))}
          </div>

          {isClubMember ? (
            <div style={{
              background: '#1c3d2a30', border: '1px solid #3fb950',
              borderRadius: 8, padding: '8px 16px', textAlign: 'center',
              color: '#3fb950', fontSize: 13, fontWeight: 600
            }}>Active Member</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => openVenmo(4.99, 'Euchre Table Club Membership - Monthly')}
                style={{
                  width: '100%', padding: '12px',
                  background: '#3fb950', color: '#000', border: 'none',
                  borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer'
                }}
              >
                Subscribe Monthly — $4.99
              </button>
              <button
                onClick={() => openVenmo(39.99, 'Euchre Table Club Membership - Annual')}
                style={{
                  width: '100%', padding: '12px',
                  background: 'transparent', color: '#3fb950', border: '1px solid #3fb950',
                  borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}
              >
                Subscribe Annually — $39.99/yr
              </button>
              <div style={{ color: '#6b7280', fontSize: 11, textAlign: 'center', marginTop: 4 }}>
                Payment via Venmo to <span style={{ color: '#008CFF' }}>@{VENMO_HANDLE}</span>.
                Your membership will be activated within 24 hours.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Promo code section */}
      {!isClubMember && (
        <div style={{
          background: '#161b22', border: '1px solid #30363d',
          borderRadius: 12, padding: isMobile ? 16 : 24, marginBottom: 16
        }}>
          <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>
            Have a promo code?
          </div>
          <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 14 }}>
            Enter a promo code to activate Club membership or add Bucks to your account.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Enter promo code"
              value={promoCode}
              onChange={e => setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleRedeemPromo()}
              style={{
                flex: 1, minWidth: 160, padding: '10px 14px',
                background: '#21262d', border: '1px solid #30363d',
                borderRadius: 8, color: '#e2e8f0', fontSize: 15,
                fontFamily: 'monospace', letterSpacing: 2, outline: 'none'
              }}
            />
            <button
              onClick={handleRedeemPromo}
              disabled={promoLoading || !promoCode.trim()}
              style={{
                padding: '10px 20px', background: promoLoading || !promoCode.trim() ? '#21262d' : '#7c3aed',
                color: promoLoading || !promoCode.trim() ? '#484f58' : '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                cursor: promoLoading || !promoCode.trim() ? 'default' : 'pointer',
                transition: 'background 0.2s'
              }}
            >
              {promoLoading ? 'Applying…' : 'Apply Code'}
            </button>
          </div>
          {promoStatus && (
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: 8,
              background: promoStatus.success ? '#1c3d2a' : '#3d1c1c',
              border: `1px solid ${promoStatus.success ? '#3fb950' : '#f85149'}`,
              color: promoStatus.success ? '#3fb950' : '#f85149',
              fontSize: 13
            }}>
              {promoStatus.message}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
